import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeStorage } from '@/utils/safeStorage';

export type UnitSystem = 'metric' | 'imperial';

interface UnitConversions {
  celsiusToFahrenheit: (celsius: number) => number;
  mmToInches: (mm: number) => number;
  kmhToMph: (kmh: number) => number;
}

export const useUnitPreferences = () => {
  const [preferredUnits, setPreferredUnits] = useState<UnitSystem>('imperial');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Unit conversion functions
  const conversions: UnitConversions = {
    celsiusToFahrenheit: (celsius: number) => (celsius * 9/5) + 32,
    mmToInches: (mm: number) => mm * 0.0393701,
    kmhToMph: (kmh: number) => kmh * 0.621371
  };

  // Load preferences on component mount
  useEffect(() => {
    if (typeof window === 'undefined') { setLoading(false); return; }
    const loadPreferences = async () => {
      if (user) {
        // Load from user profile
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_units')
            .eq('user_id', user.id)
            .single();

          if (profile?.preferred_units) {
            setPreferredUnits(profile.preferred_units as UnitSystem);
            
            // Migrate guest preference to user profile if localStorage has a different value
            const guestPreference = safeStorage.getItem('preferred_units') as UnitSystem;
            if (guestPreference && guestPreference !== profile.preferred_units) {
              await updatePreferences(guestPreference);
              safeStorage.removeItem('preferred_units');
            }
          } else {
            // No preference set, check localStorage for guest preference
            const guestPreference = safeStorage.getItem('preferred_units') as UnitSystem;
            if (guestPreference) {
              await updatePreferences(guestPreference);
              setPreferredUnits(guestPreference);
              safeStorage.removeItem('preferred_units');
            }
          }
        } catch (error) {
          console.error('Error loading unit preferences:', error);
          // Fallback to localStorage for guests
          const guestPreference = safeStorage.getItem('preferred_units') as UnitSystem;
          if (guestPreference) {
            setPreferredUnits(guestPreference);
          }
        }
      } else {
        // Guest user - load from localStorage
        const guestPreference = safeStorage.getItem('preferred_units') as UnitSystem;
        if (guestPreference) {
          setPreferredUnits(guestPreference);
        }
      }
      
      setLoading(false);
    };

    loadPreferences();
  }, [user]);

  // Update preferences
  const updatePreferences = useCallback(async (newUnits: UnitSystem) => {
    setPreferredUnits(newUnits);

    if (user) {
      // Save to user profile
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ preferred_units: newUnits })
          .eq('user_id', user.id);

        if (error) {
          console.error('Error updating unit preferences:', error);
          // Fallback to localStorage
          safeStorage.setItem('preferred_units', newUnits);
        }
      } catch (error) {
        console.error('Error updating unit preferences:', error);
        safeStorage.setItem('preferred_units', newUnits);
      }
    } else {
      // Save to localStorage for guests
      safeStorage.setItem('preferred_units', newUnits);
    }
  }, [user]);

  // Format temperature with unit
  const formatTemperature = useCallback((celsius: number, showUnit = true) => {
    const value = preferredUnits === 'imperial' 
      ? conversions.celsiusToFahrenheit(celsius)
      : celsius;
    
    const unit = preferredUnits === 'imperial' ? '°F' : '°C';
    return showUnit ? `${Math.round(value)}${unit}` : Math.round(value).toString();
  }, [preferredUnits]);

  // Format precipitation with unit
  const formatPrecipitation = useCallback((mm: number, showUnit = true) => {
    const value = preferredUnits === 'imperial' 
      ? conversions.mmToInches(mm)
      : mm;
    
    const unit = preferredUnits === 'imperial' ? ' in' : ' mm';
    const decimals = preferredUnits === 'imperial' ? 2 : 0;
    return showUnit ? `${value.toFixed(decimals)}${unit}` : value.toFixed(decimals);
  }, [preferredUnits]);

  // Format wind speed with unit
  const formatWindSpeed = useCallback((kmh: number, showUnit = true) => {
    const value = preferredUnits === 'imperial' 
      ? conversions.kmhToMph(kmh)
      : kmh;
    
    const unit = preferredUnits === 'imperial' ? ' mph' : ' km/h';
    return showUnit ? `${Math.round(value)}${unit}` : Math.round(value).toString();
  }, [preferredUnits]);

  return {
    preferredUnits,
    setPreferredUnits: updatePreferences,
    loading,
    formatTemperature,
    formatPrecipitation,
    formatWindSpeed,
    conversions
  };
};